import React, { useEffect, useState } from "react";
import { X, Star, Mail, Phone, Shield, Ban, Trash2, MessageSquare,
         Wallet, ClipboardList, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAdminUi } from "@/context/AdminUiContext";

function Chip({ children, tone = "slate" }) {
  const tones = {
    teal:   "bg-teal-500/15 text-teal-200 border-teal-400/30",
    amber:  "bg-amber-500/15 text-amber-200 border-amber-400/40",
    red:    "bg-red-500/15 text-red-300 border-red-400/40",
    slate:  "bg-slate-500/15 text-slate-200 border-slate-400/30",
    blue:   "bg-blue-500/15 text-blue-200 border-blue-400/40",
    green:  "bg-emerald-500/15 text-emerald-200 border-emerald-400/40",
  };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-[0.14em] ${tones[tone]}`}>{children}</span>;
}

function Section({ title, testid, children }) {
  return (
    <section data-testid={testid} className="mt-6">
      <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-teal-300 mb-2">{title}</div>
      {children}
    </section>
  );
}

export default function AdminUserProfileDrawer() {
  const { userId, closeDrawers, openBooking, notifyChanged } = useAdminUi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [showBlock, setShowBlock] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [note, setNote] = useState("");

  const load = async () => {
    if (!userId) { setData(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load profile");
      closeDrawers();
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  if (!userId) return null;

  const u = data?.user;

  const doBlock = async () => {
    if ((blockReason || "").trim().length < 3) { toast.error("Give a short reason (3+ chars)"); return; }
    try {
      const { data: res } = await api.post(`/admin/users/${userId}/block`, { reason: blockReason.trim() });
      toast.success("Account blocked");
      setShowBlock(false); setBlockReason("");
      if (res.active_jobs_requiring_attention?.length) {
        toast.warning(`${res.active_jobs_requiring_attention.length} active jobs need reassignment.`);
      }
      load(); notifyChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Block failed"); }
  };
  const doUnblock = async () => {
    try {
      await api.post(`/admin/users/${userId}/unblock`, {});
      toast.success("Account unblocked");
      load(); notifyChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Unblock failed"); }
  };
  const doDelete = async () => {
    if (deleteConfirm !== "DELETE") { toast.error("Type DELETE to confirm"); return; }
    try {
      await api.delete(`/admin/users/${userId}`);
      toast.success("Profile deleted (anonymized) — historical records preserved");
      setShowDelete(false); setDeleteConfirm("");
      load(); notifyChanged(); closeDrawers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await api.post(`/admin/users/${userId}/admin-notes`, { note: note.trim() });
      toast.success("Note added");
      setNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add note"); }
  };

  const cp = data?.cleaner_profile;
  const cv = data?.cleaner_vehicle;
  const ci = data?.cleaner_insurance;
  const isCleaner = u?.role === "cleaner";

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" onClick={closeDrawers} data-testid="admin-drawer-overlay" />
      <aside
        className="fixed inset-y-0 right-0 z-50 w-full sm:w-[720px] bg-slate-950 border-l border-white/10 overflow-y-auto text-slate-100"
        data-testid="admin-user-drawer"
        role="dialog" aria-modal="true"
      >
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b border-white/5 bg-slate-950/95 backdrop-blur">
          <div className="min-w-0">
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-teal-300">Admin · Profile</div>
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <div className="font-display font-bold text-lg truncate">{u?.full_name || "—"}</div>
              {u?.role && <Chip tone="teal">{u.role}</Chip>}
              {data?.blocked && <Chip tone="red"><Ban className="w-3 h-3" />blocked</Chip>}
              {data?.deleted && <Chip tone="slate"><Trash2 className="w-3 h-3" />deleted</Chip>}
            </div>
          </div>
          <button onClick={closeDrawers} data-testid="admin-drawer-close" className="p-2 rounded-lg hover:bg-white/10 text-slate-300">
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading || !data ? (
          <div className="p-8 text-slate-400">Loading profile…</div>
        ) : (
          <div className="px-6 pb-24 pt-2">
            {/* Header details */}
            <div className="grid sm:grid-cols-2 gap-4 mt-4 text-sm">
              <div className="rounded-xl border border-white/10 p-4 bg-white/[0.02]">
                {cp?.photo_url && <img src={cp.photo_url} alt="" className="w-16 h-16 rounded-full border border-teal-400/40 mb-3 object-cover" />}
                <div className="flex items-center gap-2 text-slate-300"><Mail className="w-3.5 h-3.5" /> {u?.email}</div>
                {cp?.phone && <div className="flex items-center gap-2 text-slate-300 mt-1"><Phone className="w-3.5 h-3.5" /> {cp.phone}</div>}
                <div className="text-xs text-slate-500 mt-2">Joined {u?.created_at ? new Date(u.created_at).toLocaleString() : "—"}</div>
                {isCleaner && cp?.cleanbaz_id && (
                  <div className="mt-2 inline-block font-mono text-[10px] px-2 py-0.5 rounded bg-teal-500/15 text-teal-200 border border-teal-400/30" data-testid="drawer-cleanbaz-id">
                    {cp.cleanbaz_id}
                  </div>
                )}
              </div>
              <div className="rounded-xl border border-white/10 p-4 bg-white/[0.02] text-xs space-y-1.5">
                {isCleaner ? (
                  <>
                    <div>ID verification: <Chip tone={cp?.verification_status === "approved" ? "teal" : "amber"}>{cp?.verification_status || "unverified"}</Chip></div>
                    <div>Account: <Chip tone={cp?.account_status === "active" ? "green" : "amber"}>{cp?.account_status || "—"}</Chip></div>
                    <div>Agreement accepted: <span className="font-mono">{cp?.agreement_accepted_version || "not accepted"}</span></div>
                    <div>Zones: <span className="font-mono">{(cp?.postal_codes || []).join(", ") || "—"}</span></div>
                    <div>Travel farther: <span className="font-mono">{cp?.willing_to_travel ? `yes${cp?.max_travel_miles ? ` · ${cp.max_travel_miles} mi` : ""}` : "no"}</span></div>
                    <div>Categories: <span className="font-mono">{(cp?.service_categories || []).join(", ") || "—"}</span></div>
                    <div>Stripe Connect: {cp?.stripe_account_id
                      ? <Chip tone="teal">connected · {String(cp.stripe_account_id).slice(-6)}</Chip>
                      : <Chip tone="slate">not connected</Chip>}
                    </div>
                    <div>Rating: <span className="font-mono text-teal-300">{data.avg_rating ?? cp?.avg_rating ?? 0} <Star className="w-3 h-3 inline fill-amber-400 text-amber-400" /></span></div>
                  </>
                ) : (
                  <>
                    <div>Segment: <Chip tone={data.bookings_summary?.as_client?.total ? "teal" : "slate"}>customer</Chip></div>
                    <div>Total bookings: <span className="font-mono">{data.bookings_summary?.as_client?.total || 0}</span></div>
                    <div>Completed: <span className="font-mono">{data.bookings_summary?.as_client?.completed || 0}</span></div>
                    <div>Cancelled: <span className="font-mono">{data.bookings_summary?.as_client?.cancelled || 0}</span></div>
                    <div>Active: <span className="font-mono">{data.bookings_summary?.as_client?.active || 0}</span></div>
                    <div>Total paid: <span className="font-mono text-teal-300">${(data.bookings_summary?.as_client?.total_paid || 0).toFixed(2)}</span></div>
                  </>
                )}
              </div>
            </div>

            {/* Admin action buttons */}
            <div className="mt-6 flex flex-wrap gap-2" data-testid="drawer-actions">
              {!data.blocked ? (
                <button data-testid="drawer-block-btn" onClick={() => setShowBlock(true)} className="px-3 py-2 rounded-lg border border-amber-400/40 text-amber-200 hover:bg-amber-500/10 text-xs font-mono uppercase tracking-[0.16em] inline-flex items-center gap-1.5">
                  <Ban className="w-3.5 h-3.5" /> Block user
                </button>
              ) : (
                <button data-testid="drawer-unblock-btn" onClick={doUnblock} className="px-3 py-2 rounded-lg border border-teal-400/40 text-teal-200 hover:bg-teal-500/10 text-xs font-mono uppercase tracking-[0.16em] inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Unblock
                </button>
              )}
              <button data-testid="drawer-delete-btn" onClick={() => setShowDelete(true)} className="px-3 py-2 rounded-lg border border-red-400/40 text-red-300 hover:bg-red-500/10 text-xs font-mono uppercase tracking-[0.16em] inline-flex items-center gap-1.5">
                <Trash2 className="w-3.5 h-3.5" /> Delete profile
              </button>
            </div>

            {data.block_info && (
              <div className="mt-4 rounded-xl border border-red-400/30 bg-red-500/10 p-4 text-sm" data-testid="drawer-block-info">
                <div className="flex items-center gap-2 text-red-200 font-semibold"><AlertTriangle className="w-4 h-4" /> Currently blocked</div>
                <div className="text-xs text-red-200/80 mt-1">By {data.block_info.blocked_by || "admin"} on {new Date(data.block_info.blocked_at).toLocaleString()} · Reason: {data.block_info.blocked_reason}</div>
              </div>
            )}

            {/* Block confirm */}
            {showBlock && (
              <div className="mt-4 rounded-xl border border-amber-400/30 bg-amber-500/[0.06] p-4" data-testid="drawer-block-form">
                <div className="text-sm font-semibold text-amber-200 mb-2">Reason for block (will be shown to admins only)</div>
                <textarea value={blockReason} onChange={(e) => setBlockReason(e.target.value)} rows={3} data-testid="drawer-block-reason" placeholder="Fraudulent activity / repeated no-show / verification failure…" className="w-full bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100" />
                <div className="mt-2 flex gap-2 justify-end">
                  <button onClick={() => { setShowBlock(false); setBlockReason(""); }} className="px-3 py-1.5 text-xs rounded-md border border-white/10 text-slate-300">Cancel</button>
                  <button onClick={doBlock} data-testid="drawer-block-confirm" className="px-3 py-1.5 text-xs rounded-md bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">Confirm block</button>
                </div>
              </div>
            )}

            {/* Delete confirm */}
            {showDelete && (
              <div className="mt-4 rounded-xl border border-red-400/40 bg-red-500/[0.06] p-4" data-testid="drawer-delete-form">
                <div className="text-sm font-semibold text-red-200 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Delete this profile? This action cannot be easily undone.</div>
                <div className="text-xs text-red-200/80 mt-2">
                  The profile will be anonymized so the user can no longer log in. <b>Historical records preserved:</b> bookings, payments, complaints, disputes, audit log, and Stripe Connect account. Stripe Connect is <b>not</b> deleted from Stripe.
                </div>
                <div className="mt-3">
                  <label className="text-[10px] font-mono uppercase tracking-widest text-red-200">Type <b>DELETE</b> to confirm</label>
                  <input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} data-testid="drawer-delete-confirm-input" className="mt-1 w-full bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 font-mono" />
                </div>
                <div className="mt-3 flex gap-2 justify-end">
                  <button onClick={() => { setShowDelete(false); setDeleteConfirm(""); }} className="px-3 py-1.5 text-xs rounded-md border border-white/10 text-slate-300">Cancel</button>
                  <button onClick={doDelete} data-testid="drawer-delete-confirm" className="px-3 py-1.5 text-xs rounded-md bg-red-500 hover:bg-red-400 text-white font-semibold">Delete permanently</button>
                </div>
              </div>
            )}

            {/* Vehicle + Insurance (cleaner only, admin-only visibility) */}
            {isCleaner && (cv || ci) && (
              <Section title="Vehicle & auto insurance (admin-only)" testid="drawer-section-vehicle-insurance">
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 text-xs space-y-3">
                  {cv && (
                    <div data-testid="drawer-cleaner-vehicle">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">Vehicle</div>
                      <div className="grid sm:grid-cols-2 gap-y-1 gap-x-4">
                        <div>Transport: <span className="font-mono">{cv.transportation_type || "—"}</span></div>
                        <div>Primary: <span className="font-mono">{cv.primary_vehicle ? "yes" : "no"}</span></div>
                        <div>Type: <span className="font-mono">{cv.vehicle_type || "—"}</span></div>
                        <div>Year / Make / Model: <span className="font-mono">{[cv.year, cv.make, cv.model].filter(Boolean).join(" ") || "—"}</span></div>
                        <div>Plate: <span className="font-mono">{cv.plate_state ? `${cv.plate_state} ${cv.license_plate || ""}` : (cv.license_plate || "—")}</span></div>
                        <div>VIN (last 6): <span className="font-mono">{cv.vin_last6 || "—"}</span></div>
                        <div>DL state / exp: <span className="font-mono">{cv.dl_state || "—"} · {cv.dl_expiration_date || "—"}</span></div>
                        <div>DL status: <Chip tone={cv.dl_status === "verified" ? "green" : cv.dl_status === "rejected" ? "red" : "amber"}>{cv.dl_status || "not_submitted"}</Chip></div>
                      </div>
                    </div>
                  )}
                  {ci && (
                    <div data-testid="drawer-cleaner-insurance" className="pt-2 border-t border-white/5">
                      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-1">Auto insurance</div>
                      <div className="grid sm:grid-cols-2 gap-y-1 gap-x-4">
                        <div>Carrier: <span className="font-mono">{ci.carrier || "—"}</span></div>
                        <div>Policy: <span className="font-mono">••••{ci.policy_number_last4 || "—"}</span></div>
                        <div>State: <span className="font-mono">{ci.insurance_state || "—"}</span></div>
                        <div>Effective: <span className="font-mono">{ci.effective_date || "—"}</span></div>
                        <div>Expires: <span className="font-mono">{ci.expiration_date || "—"}</span></div>
                        <div>Status: <Chip tone={ci.status === "approved" ? "green" : ci.status === "rejected" ? "red" : "amber"}>{ci.status || "—"}</Chip></div>
                        <div>Business-use ack: <span className="font-mono">{ci.understands_business_use_ack ? "yes" : "no"}</span></div>
                        <div>Coverage ack: <span className="font-mono">{ci.maintains_required_coverage_ack ? "yes" : "no"}</span></div>
                        <div className="sm:col-span-2 text-[11px] text-slate-500">Full policy number requires the audit-logged endpoint. Proof document accessible via Admin → Risk & Safety.</div>
                      </div>
                    </div>
                  )}
                </div>
              </Section>
            )}

            {/* Admin notes */}
            <Section title="Admin notes" testid="drawer-section-notes">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2">
                {(data.admin_notes || []).length === 0 && <div className="text-xs text-slate-500">No notes yet.</div>}
                {(data.admin_notes || []).map((n, i) => (
                  <div key={i} className="text-xs bg-slate-900/60 border border-white/5 rounded p-2">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{n.actor_email} · {n.at ? new Date(n.at).toLocaleString() : ""}</div>
                    <div className="mt-1 text-slate-200 whitespace-pre-wrap">{n.note}</div>
                  </div>
                ))}
                <div className="pt-2">
                  <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="drawer-note-input" className="w-full bg-slate-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100" placeholder="Add an internal admin note…" />
                  <div className="mt-1 flex justify-end">
                    <button onClick={addNote} data-testid="drawer-note-submit" className="px-3 py-1 text-[11px] rounded-md bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold"><MessageSquare className="w-3 h-3 inline mr-1" />Add note</button>
                  </div>
                </div>
              </div>
            </Section>

            {/* Bookings */}
            <Section title={isCleaner ? "Assigned bookings" : "All bookings"} testid="drawer-section-bookings">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="text-[9px] font-mono uppercase tracking-widest text-slate-400 border-b border-white/10">
                    <tr>
                      <th className="text-left p-2">Booking</th>
                      <th className="text-left p-2">Scheduled</th>
                      <th className="text-left p-2">Status</th>
                      <th className="text-right p-2">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(isCleaner ? data.bookings_as_cleaner : data.bookings_as_client).slice(0, 100).map((j) => (
                      <tr key={j.id} className="border-t border-white/5 hover:bg-white/[0.03] cursor-pointer" onClick={() => openBooking(j.id)} data-testid={`drawer-booking-row-${j.id}`}>
                        <td className="p-2 font-mono text-teal-300">{j.id.slice(0, 8)}</td>
                        <td className="p-2 text-slate-300">{j.scheduled_at ? new Date(j.scheduled_at).toLocaleString() : "—"}</td>
                        <td className="p-2"><Chip tone={j.status === "completed" ? "green" : j.status === "cancelled" ? "red" : "amber"}>{j.status}</Chip></td>
                        <td className="p-2 text-right font-mono text-slate-100">${Number(j.total || 0).toFixed(2)}</td>
                      </tr>
                    ))}
                    {(isCleaner ? data.bookings_as_cleaner : data.bookings_as_client).length === 0 && (
                      <tr><td colSpan={4} className="p-4 text-center text-slate-500">No bookings on record.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Section>

            {isCleaner && (
              <Section title="Withdrawals / payout history" testid="drawer-section-payouts">
                <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                  {(data.withdrawals || []).length === 0 ? (
                    <div className="text-xs text-slate-500 flex items-center gap-2"><Wallet className="w-3 h-3" />No withdrawals yet.</div>
                  ) : (
                    <ul className="space-y-1 text-xs">
                      {data.withdrawals.map((w, i) => (
                        <li key={i} className="flex items-center justify-between border-b border-white/5 py-1">
                          <span className="text-slate-300">{w.created_at ? new Date(w.created_at).toLocaleString() : "—"}</span>
                          <span className="font-mono text-teal-300">${Number(w.amount || 0).toFixed(2)}</span>
                          <Chip tone={w.status === "paid" || w.status === "processed" ? "green" : w.status === "failed" ? "red" : "amber"}>{w.status}</Chip>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </Section>
            )}

            <Section title="Payment history" testid="drawer-section-payments">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-xs">
                {(data.payment_history || []).length === 0 ? (
                  <div className="text-slate-500">No transactions on record.</div>
                ) : (
                  <ul className="space-y-1">
                    {data.payment_history.map((p, i) => (
                      <li key={i} className="flex items-center justify-between border-b border-white/5 py-1">
                        <span className="text-slate-300">{p.created_at ? new Date(p.created_at).toLocaleString() : "—"} · {p.type || "charge"}</span>
                        <span className="font-mono text-slate-100">${Number(p.amount || 0).toFixed(2)}</span>
                        <Chip tone={p.status === "succeeded" || p.status === "paid" ? "green" : p.status === "refunded" ? "amber" : "slate"}>{p.status}</Chip>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Section>

            {(data.refund_history || []).length > 0 && (
              <Section title="Refund history" testid="drawer-section-refunds">
                <ul className="text-xs space-y-1">
                  {data.refund_history.map((r, i) => (
                    <li key={i} className="rounded border border-white/10 p-2 flex items-center justify-between">
                      <span className="text-slate-300">{r.created_at ? new Date(r.created_at).toLocaleString() : "—"} · {r.reason || "—"}</span>
                      <span className="font-mono text-amber-300">${Number(r.amount || 0).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              </Section>
            )}

            <Section title="Complaints / disputes" testid="drawer-section-complaints">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-xs space-y-2">
                {[...(data.complaints_as_customer || []), ...(data.complaints_against_cleaner || [])].length === 0 && (
                  <div className="text-slate-500">No complaints on record.</div>
                )}
                {[...(data.complaints_as_customer || []).map(c => ({...c, dir: "filed by user"})),
                  ...(data.complaints_against_cleaner || []).map(c => ({...c, dir: "against user"}))].map((c, i) => (
                  <div key={i} className="border border-white/5 rounded p-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Chip tone={c.status === "under_review" ? "amber" : "slate"}>{c.status || "—"}</Chip>
                      <span className="text-[10px] font-mono text-slate-400">{c.dir}</span>
                      {c.job_id && (
                        <button onClick={() => openBooking(c.job_id)} data-testid={`drawer-complaint-booking-${c.job_id}`} className="text-teal-300 hover:underline font-mono text-[11px]">booking {c.job_id.slice(0, 8)}</button>
                      )}
                    </div>
                    <div className="mt-1 text-slate-200">{c.issue_type || "—"}</div>
                    <div className="mt-1 text-slate-400 whitespace-pre-wrap">{c.description}</div>
                  </div>
                ))}
              </div>
            </Section>

            {(data.notifications || []).length > 0 && (
              <Section title="Notifications" testid="drawer-section-notifications">
                <ul className="text-xs space-y-1">
                  {data.notifications.slice(0, 30).map((n, i) => (
                    <li key={i} className="rounded border border-white/10 p-2">
                      <div className="text-[10px] font-mono text-slate-400">{n.created_at ? new Date(n.created_at).toLocaleString() : ""} · {n.channel || "—"}</div>
                      <div className="text-slate-200">{n.title || n.body || n.message || "—"}</div>
                    </li>
                  ))}
                </ul>
              </Section>
            )}
          </div>
        )}
      </aside>
    </>
  );
}
